import { DIMENSION_KEYS } from './types';
import type {
  AppContent,
  AssessmentModel,
  CapabilityPack,
  ContentManifest,
  ManifestLocale,
  Mode,
  Scenario,
  ScenarioPack,
  UiCopy,
} from './types';

const MANIFEST_PATH = '/content/manifest.v1.json';

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`content_fetch_failed:${response.status}:${path}`);
  return (await response.json()) as T;
}

export function resolveLocale(manifest: ContentManifest, requested?: string): ManifestLocale {
  const candidates = [requested, ...(typeof navigator !== 'undefined' ? navigator.languages : [])]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase());

  for (const candidate of candidates) {
    const exact = manifest.locales.find((entry) => entry.code.toLowerCase() === candidate);
    if (exact) return exact;
    if (candidate.startsWith('zh')) {
      const chinese = manifest.locales.find((entry) => entry.code === 'zh-CN');
      if (chinese) return chinese;
    }
    const language = candidate.split('-')[0];
    const partial = manifest.locales.find(
      (entry) => entry.code.toLowerCase().split('-')[0] === language,
    );
    if (partial) return partial;
  }

  const fallback = manifest.locales.find((entry) => entry.code === manifest.defaultLocale);
  if (!fallback) throw new Error('default_locale_missing');
  return fallback;
}

export function mergeScenarios(model: AssessmentModel, pack: ScenarioPack): Scenario[] {
  const localizedById = new Map(pack.scenarios.map((scenario) => [scenario.id, scenario]));

  return model.scenarios.map((modelScenario) => {
    const localized = localizedById.get(modelScenario.id);
    if (!localized) throw new Error(`localized_scenario_missing:${modelScenario.id}`);

    const localizedChoices = new Map(localized.choices.map((choice) => [choice.id, choice]));
    const choices = modelScenario.choices.map((modelChoice) => {
      const localizedChoice = localizedChoices.get(modelChoice.id);
      if (!localizedChoice) {
        throw new Error(`localized_choice_missing:${modelScenario.id}:${modelChoice.id}`);
      }
      return { ...localizedChoice, ...modelChoice };
    });

    if (localizedChoices.size !== choices.length) {
      throw new Error(`localized_choice_extra:${modelScenario.id}`);
    }

    return {
      ...localized,
      construct: modelScenario.construct,
      modes: modelScenario.modes,
      choices,
    };
  });
}

function validateContent(content: AppContent): AppContent {
  if (content.manifest.schemaVersion !== 1 || content.model.schemaVersion !== 1) {
    throw new Error('unsupported_content_schema');
  }
  if (content.ui.locale !== content.locale.code) throw new Error('ui_locale_mismatch');
  if (content.scenarios.length !== content.model.scenarios.length) {
    throw new Error('scenario_count_mismatch');
  }
  if (content.capabilities.length < 12) throw new Error('capability_map_incomplete');
  if (content.model.dimensions.join('|') !== DIMENSION_KEYS.join('|')) {
    throw new Error('dimension_contract_mismatch');
  }
  return content;
}

export async function loadManifest(): Promise<ContentManifest> {
  return getJson<ContentManifest>(MANIFEST_PATH);
}

export async function loadContent(requestedLocale?: string): Promise<AppContent> {
  const manifest = await loadManifest();
  const locale = resolveLocale(manifest, requestedLocale);
  const [model, ui, scenarioPack, capabilityPack] = await Promise.all([
    getJson<AssessmentModel>(manifest.modelPath),
    getJson<UiCopy>(locale.uiPath),
    getJson<ScenarioPack>(locale.scenariosPath),
    getJson<CapabilityPack>(locale.capabilitiesPath),
  ]);

  const content: AppContent = {
    manifest,
    locale,
    model,
    ui,
    scenarios: mergeScenarios(model, scenarioPack),
    capabilities: capabilityPack.items,
    ...(capabilityPack.review ? { capabilityReview: capabilityPack.review } : {}),
  };
  return validateContent(content);
}

export function scenariosForMode(scenarios: Scenario[], mode: Mode): Scenario[] {
  return scenarios.filter((scenario) => scenario.modes.includes(mode));
}

export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? `{${key}}`));
}
